/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import util from "../../util/util.js";
import ko from "knockout";

function applyToObject(object, predicate, defaultValue) {
	var predicateType = typeof predicate;
	if (predicateType == "function")    // Given a function; run it against the data value
		return predicate(object);
	else if (predicateType == "string") // Given a string; treat it as a property name on the data value
		return object[predicate];
	else                                // Given no optionsText arg; use the data value itself
		return defaultValue;
}

var SearchSelectViewModel = function(params, parent) {
	var self = this;

	// params:
	//
	// options
	// optionsText
	// value
	// matchFn
	// renderFn
	// searchText
	// displayAllOptions
	// disableWhenNoOptions
	// label
	// selectedOptions
	// sortOptions
	// maxDisplayedOptions
	// showSelectedFirst
	// enabled

	params = {
		matchFn: function(option, text, search) {
			return text && search && text.toLowerCase().indexOf(search.toLowerCase());
		},
		renderFn: function(option, search) {
			var text = applyToObject(option, params.optionsText, option);
			return text;
		},
		placeholder: 'Search...',
		disableWhenNoOptions: true,
		sortOptions: true,
		...params
	};

	if(!params.options) {
		console.log("Error: options parameter required for searchable-select component");
	}
	if(!params.optionsText) {
		console.log("Error: optionsText parameter required for searchable-select component");
	}

	if(!!params.value == !!params.selectedOptions) {
		console.log("Error: searchable-select component: either 'value' and 'selectedOptions' must be provided (but not both)");
	}

	function textForOption(option) {
		return applyToObject(option, params.optionsText, option);
	}

	self.isSelected = function(option) {
		return params.selectedOptions().includes(option);
	};

	self.multiselect = !!params.selectedOptions;

	self.label = ko.observable(params.label);

	self.placeholder = params.placeholder;
	self.active = ko.observable(false);

	self.searchText = params.searchText || ko.observable('');
	self.searchHasFocus = ko.observable(false);
	self.optionsVisible = ko.observable(false);

	// Random string to prevent autocomplete
	self.fieldName = (Math.random() + 1).toString(36).substring(2);

	self.enabled = ko.pureComputed(function() {
		if(params.disableWhenNoOptions) {
			if((ko.unwrap(params.options) || []).length == 0) {
				return false;
			}
		}
		if('enabled' in params) {
			return ko.unwrap(params.enabled);
		}
		return true;
	});

	ko.computed(function() {
		self.optionsVisible(self.active() && self.displayedOptions().length > 0);
	});

	self.noMatches = ko.pureComputed(function() {
		return self.active() && self.displayedOptions().length == 0;
	});

	self.renderOption = function(option) {
		return params.renderFn(option, self.searchText());
	};

	self.isHighlighted = function(option) {
		return self.highlightedOption() == option;
	};

	self.highlightedOption = ko.observable(null);

	self.displayedOptions = ko.pureComputed(function() {
		var search = self.searchText().trim();
		var options = ko.unwrap(params.options) || [];
		var maxResults = params.maxDisplayedOptions || 40;

		var result;

		if(search && !params.displayAllOptions) {
			result = options
				.map(function(option) {
					return {
						option: option,
						match: params.matchFn(option, textForOption(option), search)
					};
				})
				.filter(function(item) {
					return item.match >= 0;
				})
				.toSorted(util.compareNumberFn(x => x.match))
				.map(x => x.option);

		} else {
			result = params.sortOptions ? util.sortBy(options, textForOption) : options;
		}

		if(params.showSelectedFirst) {
			result = result.sort((a, b) => self.isSelected(a) < self.isSelected(b));
			maxResults = Math.max(maxResults, params.selectedOptions().length);
		}

		return result.slice(0, maxResults);
	});

	function highlightFirstItem() {
		var displayedOptions = self.displayedOptions();
		if(displayedOptions.length > 0) {
			if(!self.highlightedOption() || !displayedOptions.includes(self.highlightedOption())) {
				self.highlightedOption(displayedOptions[0]);
			}
		} else {
			self.highlightedOption(null);
		}
	}

	self.completion = ko.pureComputed(function() {
		var options = self.displayedOptions();
		if(options.length == 0) return '';

		var search = self.searchText().trim();

		var option = options[0];
		var optionText = textForOption(option);

		var index = params.matchFn(option, optionText, search);
		if(index != 0) return '';

		return optionText;
	});

	function tabComplete() {
		var search = self.searchText();
		var completion = self.completion();

		if(completion) {
			var pos = search.length;
			while(completion[pos] == " " && pos < completion.length) pos++;
			while(completion[pos] != " " && pos < completion.length) pos++;

			if(pos < completion.length) pos++; // 1 more to include a trailing space

			self.searchText(completion.substring(0, pos));
		}
	}

	function cycleSelection(up) {
		var index = self.displayedOptions().indexOf(self.highlightedOption());
		var len = self.displayedOptions().length;

		if(len == 0) return;

		if(index == -1) {
			index = up ? len - 1 : 0;
		} else {
			index = (index + len + (up ? -1 : 1)) % len;
		}

		self.highlightedOption(self.displayedOptions()[index]);
	}

	function selectedOptionsAsText() {
		return params.selectedOptions().map(textForOption).toSorted().join(", ");
	}

	function cancel() {
		if(self.multiselect) {
			self.searchText(selectedOptionsAsText());
		} else {
			var option = params.value();
			var text = option ? textForOption(option) : '';
			self.searchText(text);
			self.optionsVisible(false);
			self.searchHasFocus(false);
		}
		self.optionsVisible(false);
		self.searchHasFocus(false);
	}

	ko.computed(function() {
		if(self.multiselect) {
			if(!self.searchHasFocus()) {
				self.searchText(selectedOptionsAsText());
			}
		} else {
			var option = params.value();
			var text = option ? textForOption(option) : '';
			self.searchText(text);
		}
	});

	self.searchHasFocus.subscribe(function(hasFocus) {
		setTimeout(function() {
			if(self.active() && !hasFocus) {
				cancel();
				self.active(false);
			} else if(hasFocus && !self.active()) {
				self.searchText('');
				self.active(true);
			}
		},100);
	});

	function toggleOption(option) {
		if(params.selectedOptions().includes(option)) {
			params.selectedOptions.remove(option);
		} else {
			params.selectedOptions.push(option);
		}
	}

	function selectHighlightedOption() {
		var option = self.highlightedOption();
		if(!option) return;

		if(self.multiselect) {
			toggleOption(option);
		} else {
			self.searchText(textForOption(option));
			params.value(option);
			self.optionsVisible(false);
			self.searchHasFocus(false);
		}
	}

	self.clickOption = function(item, e) {
		self.highlightedOption(item);
		selectHighlightedOption();
	};

	self.itemMouseMove = function(item, e) {
		self.highlightedOption(item);
	};

	self.searchKeyPress = function(d,e) {
		switch(e.keyCode) {
			case 9: // TAB
				if(self.searchText().length == 0) {
					return true;
				} else {
					tabComplete();
					return false;
				}

			case 38: // UP
				cycleSelection(true);
				return false;

			case 40: // DOWN
				cycleSelection(false);
				return false;

			case 13: // ENTER
				selectHighlightedOption();
				break;

			case 27: // ESC
				cancel();
				// self.optionsVisible(false);
				break;

			default:
				self.optionsVisible(true);
				setTimeout(highlightFirstItem);
				break;
		}
		return true;
	};

	self.onWheelEvent = function(vm, evt) {
		var dy = evt.deltaY;
		var scrollParent = evt.currentTarget;

		var atTop = scrollParent.scrollTop <= 0;
		var atBottom = scrollParent.scrollTop >= scrollParent.scrollHeight - scrollParent.clientHeight;

		if((atTop && dy < 0) || (atBottom && dy > 0)) {
			evt.preventDefault();
		} else {
			return true;
		}
	};

	function hasValueOrSelectedOption() {
		if(self.multiselect) {
			return params.selectedOptions().length > 0;
		} else {
			return !!params.value();
		}
	}

	self.showArrow = ko.pureComputed(function() {
		return !self.searchHasFocus() && !hasValueOrSelectedOption();
	});

	self.clickedArrow = function() {
		self.searchHasFocus(true);
	};

	self.showClear = ko.pureComputed(function() {
		return !self.searchHasFocus() && hasValueOrSelectedOption();
	});

	self.clickedClear = function() {
		if(!self.enabled()) {
			return;
		}

		cancel();
		if(params.value) {
			params.value(null);
		} else {
			params.selectedOptions([]);
		}
	};
};

ko.components.register('searchable-select', {
	viewModel: function(params) {
		return new SearchSelectViewModel(params);
	},
	template: require('../../../templates/shared/searchable-select.html')
});


export default SearchSelectViewModel;
